import "./freight-details.css";

import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { GridCustomRowProps } from "@tql/ui-components-react/kendo/grid";
import {
	Card,
	CardBody,
	CardHeader,
} from "@tql/ui-components-react/kendo/layout";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { plusCircle } from "@tql/ui-components-react/untitled/icons";
import { useAtomValue } from "jotai";
import { memo, useMemo, useState } from "react";

import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import type {
	ShipmentFreightDetails,
	ShipmentLineItem,
} from "~/shared/schemas/shipment-management-schema.ts";

import { useFreightDraft } from "../../hooks/use-shipment-draft.ts";
import { useShipmentNotifications } from "../../hooks/use-shipment-notifications.ts";
import { isEditingShipmentAtom } from "../../state/shipment-edit-atoms.ts";
import type { FreightGridRow } from "../../types/freight-grid-row.ts";
import {
	BLANK_LINE_ITEM,
	FREIGHT_DETAILS_DEFAULT,
} from "../../utils/defaults.ts";
import { EditCommoditiesModal } from "./components/edit-commodity-modal/index.tsx";
import { FreightTotalsCard } from "./components/freight-totals-card.tsx";
import { HazmatDetailRow } from "./components/hazmat-detail-row.tsx";
import { ShipmentAccessorials } from "./components/shipment-accessorials.tsx";
import { useGridColumns } from "./hooks/use-grid-columns.tsx";
import { useHazmatDetailExpand } from "./hooks/use-hazmat-detail-expand.ts";
import { useLinearFeetCalculation } from "./hooks/use-linear-feet-calculation.ts";
import { toNumber } from "./utils/calculation-utils.ts";

export type CommodityItem = ShipmentLineItem;

type CommodityModalState =
	| { mode: "add"; commodity: ShipmentLineItem }
	| { mode: "edit"; commodity: ShipmentLineItem; index: number };

// Non-hazmat rows have no nested detail, so tag them to hide the expand chevron.
const FreightRow = (props: GridCustomRowProps) => {
	const item = props.dataItem as ShipmentLineItem | undefined;
	const { trProps, children } = props;
	const className = [
		trProps?.className,
		item?.hazmat ? undefined : "non-hazmat-row",
	]
		.filter(Boolean)
		.join(" ");
	return (
		<tr {...trProps} className={className} data-testid={"freight-line-item"}>
			{children}
		</tr>
	);
};

export const FreightDetailsV2 = memo(
	({
		accessorials,
		customerId,
		details = FREIGHT_DETAILS_DEFAULT,
	}: {
		accessorials?: string[];
		customerId?: number;
		details?: ShipmentFreightDetails;
	}) => {
		const [commodityModal, setCommodityModal] =
			useState<CommodityModalState | null>(null);
		const isEditing = useAtomValue(isEditingShipmentAtom);
		const { showError } = useShipmentNotifications();
		const {
			lineItems: draftLineItems,
			freightDetails: draftFreightDetails,
			updateLineItem,
			removeLineItem,
			addLineItem,
			updateFreightDetails,
		} = useFreightDraft();

		const baseList = useMemo(
			() => draftLineItems ?? details.lineItems ?? [],
			[draftLineItems, details.lineItems],
		);

		const calculateAndUpdateLinearFeet = useLinearFeetCalculation({
			isEditing,
			updateFreightDetails,
		});

		const gridData: FreightGridRow[] = useMemo(
			() => baseList.map((item, index) => ({ ...item, rowKey: index })),
			[baseList],
		);

		const { detailExpand, handleExpandChange } =
			useHazmatDetailExpand(gridData);

		const activeFreightDetails = isEditing ? draftFreightDetails : details;
		const calculatedLinearFeet =
			activeFreightDetails?.calculatedLinearFeet ?? null;
		const rawOverwriteLinearFeet =
			activeFreightDetails?.overwriteCalculatedLinearFeet ?? null;
		const overwriteLinearFeet = rawOverwriteLinearFeet ?? calculatedLinearFeet;
		const handlingUnitsTotal = useMemo(
			() =>
				baseList.reduce((sum, item) => sum + toNumber(item.handlingUnit), 0),
			[baseList],
		);
		const totalWeight = useMemo(
			() => baseList.reduce((sum, item) => sum + toNumber(item.totalWeight), 0),
			[baseList],
		);

		// in view mode prefer the overwrite; in edit show raw calculated so the difference is visible
		const displayedLinearFeet = toNumber(
			isEditing ? calculatedLinearFeet : overwriteLinearFeet,
		);

		const handleEdit = (commodity: FreightGridRow) => {
			setCommodityModal({
				mode: "edit",
				commodity,
				index: commodity.rowKey,
			});
		};

		const handleAdd = () => {
			// Fresh object so the modal re-seeds a blank editor each time it opens.
			setCommodityModal({ mode: "add", commodity: { ...BLANK_LINE_ITEM } });
		};

		const handleRemove = (item: FreightGridRow) => {
			if (!baseList[item.rowKey]) {
				return;
			}
			// At least one commodity must remain so the shipment can still be rerated.
			if (baseList.length <= 1) {
				showError(
					"At least one commodity is required. Add another before removing this one.",
				);
				return;
			}

			removeLineItem(item.rowKey);
			void calculateAndUpdateLinearFeet(
				baseList.filter((_, index) => index !== item.rowKey),
			);
		};

		const handleClose = () => {
			setCommodityModal(null);
		};

		const handleSaveCommodity = (lineItems: Partial<ShipmentLineItem>[]) => {
			if (!commodityModal) {
				return;
			}
			if (commodityModal.mode === "add") {
				const newItems = lineItems.map((lineItem) => ({
					...BLANK_LINE_ITEM,
					...lineItem,
				}));
				for (const newItem of newItems) {
					addLineItem(newItem);
				}
				void calculateAndUpdateLinearFeet([...baseList, ...newItems]);
				return;
			}
			const { index } = commodityModal;
			const [patch] = lineItems;
			if (!baseList[index] || !patch) {
				return;
			}
			const updatedLineItems = baseList.map((item, itemIndex) =>
				itemIndex === index ? { ...item, ...patch } : item,
			);
			updateLineItem(index, patch);
			void calculateAndUpdateLinearFeet(updatedLineItems);
		};

		const columns = useGridColumns({
			isEditing,
			onEdit: handleEdit,
			onRemoveFromList: baseList.length > 1 ? handleRemove : undefined,
		});
		return (
			<div
				className={"card-wrapper"}
				data-testid={"shipment-management-freight-details"}
			>
				<EditCommoditiesModal
					commodity={commodityModal?.commodity ?? null}
					customerId={customerId}
					handlingUnitNumber={
						commodityModal?.mode === "edit"
							? commodityModal.index + 1
							: baseList.length + 1
					}
					isOpen={commodityModal !== null}
					mode={commodityModal?.mode ?? "edit"}
					onClose={handleClose}
					onSave={handleSaveCommodity}
				/>

				<Card>
					<CardHeader>
						<h3>Freight Details</h3>
					</CardHeader>
					<CardBody className={"freight-details-body"}>
						<TraxGrid
							className={"freight-hazmat-grid"}
							columns={columns}
							data={gridData}
							dataItemKey={"rowKey"}
							detail={HazmatDetailRow}
							detailExpand={detailExpand}
							editable={false}
							filterable={false}
							onDetailExpandChange={handleExpandChange}
							pageable={false}
							resizable={false}
							rows={{ data: FreightRow }}
							selectable={false}
							sortable={false}
						/>
						{isEditing ? (
							<div className={"add-commodity-row"}>
								<Button
									aria-label={"Add Handling Unit"}
									onClick={handleAdd}
									startIcon={<SvgIcon icon={plusCircle} />}
								>
									Handling Unit
								</Button>
							</div>
						) : null}
						<div className={"freight-summary"}>
							<FreightTotalsCard
								calculatedLinearFeet={displayedLinearFeet}
								handlingUnits={handlingUnitsTotal}
								isEditing={isEditing}
								onOverwriteChange={(value) =>
									updateFreightDetails({ overwriteCalculatedLinearFeet: value })
								}
								overwriteLinearFeet={
									isEditing ? rawOverwriteLinearFeet : overwriteLinearFeet
								}
								totalWeight={totalWeight}
							/>
							<ShipmentAccessorials
								accessorials={accessorials}
								isEditing={isEditing}
							/>
						</div>
					</CardBody>
				</Card>
			</div>
		);
	},
);

FreightDetailsV2.displayName = "FreightDetailsV2";
